// src/components/kit/microsite/themes.ts
//
// Layout presets, color palettes, font themes, link categories,
// SVG icons, and other constants for the microsite feature.
// Ported from the microsite mockup application.

import type {
  LayoutPreset,
  PaletteId,
  FontThemeId,
  ButtonStyle,
  ProfileDisplayMode,
  LinkCategory,
  MicrositeColors,
  MicrositeFormState,
} from '@/types/microsite';

// ============================================================
// Layout Presets
// ============================================================

export interface LayoutPresetConfig {
  id: LayoutPreset;
  name: string;
  description: string;
}

export const LAYOUT_PRESETS: Record<LayoutPreset, LayoutPresetConfig> = {
  bold: {
    id: 'bold',
    name: 'Bold',
    description: 'Full-width hero with dramatic, editorial feel',
  },
  split: {
    id: 'split',
    name: 'Split',
    description: 'Two-column: fixed image left, scrolling content right',
  },
  cardGrid: {
    id: 'cardGrid',
    name: 'Card Grid',
    description: 'Hero at top, links as a grid of cards below',
  },
};

// ============================================================
// Color Palettes
// ============================================================

export interface ColorPaletteConfig {
  id: PaletteId;
  name: string;
  colors: MicrositeColors;
}

export const COLOR_PALETTES: Record<Exclude<PaletteId, 'custom'>, ColorPaletteConfig> = {
  midnight: {
    id: 'midnight',
    name: 'Midnight',
    colors: {
      background: '#0f0f1a',
      surface: '#1a1a2e',
      primary: '#e0d6ff',
      accent: '#8b5cf6',
      text: '#f0f0f0',
      textMuted: '#a0a0b8',
      buttonBg: '#8b5cf6',
      buttonText: '#ffffff',
    },
  },
  warmSand: {
    id: 'warmSand',
    name: 'Warm Sand',
    colors: {
      background: '#faf6f1',
      surface: '#ffffff',
      primary: '#c8956c',
      accent: '#a0522d',
      text: '#2c2c2c',
      textMuted: '#7a7068',
      buttonBg: '#a0522d',
      buttonText: '#ffffff',
    },
  },
  ocean: {
    id: 'ocean',
    name: 'Ocean',
    colors: {
      background: '#0c1b2a',
      surface: '#132d46',
      primary: '#4fc3f7',
      accent: '#0288d1',
      text: '#e8f4f8',
      textMuted: '#8ab4c8',
      buttonBg: '#0288d1',
      buttonText: '#ffffff',
    },
  },
  slate: {
    id: 'slate',
    name: 'Slate',
    colors: {
      background: '#f5f5f5',
      surface: '#ffffff',
      primary: '#475569',
      accent: '#1e293b',
      text: '#1e293b',
      textMuted: '#64748b',
      buttonBg: '#1e293b',
      buttonText: '#ffffff',
    },
  },
  ember: {
    id: 'ember',
    name: 'Ember',
    colors: {
      background: '#1a1110',
      surface: '#2d1f1e',
      primary: '#ff6b35',
      accent: '#ff4444',
      text: '#f5e6e0',
      textMuted: '#c0a89e',
      buttonBg: '#ff4444',
      buttonText: '#ffffff',
    },
  },
  forest: {
    id: 'forest',
    name: 'Forest',
    colors: {
      background: '#0d1f0d',
      surface: '#1a2e1a',
      primary: '#4ade80',
      accent: '#22c55e',
      text: '#e8f5e8',
      textMuted: '#8cb88c',
      buttonBg: '#22c55e',
      buttonText: '#0d1f0d',
    },
  },
  blush: {
    id: 'blush',
    name: 'Blush',
    colors: {
      background: '#fff5f5',
      surface: '#ffffff',
      primary: '#e88da0',
      accent: '#c2185b',
      text: '#3d2c2c',
      textMuted: '#8a6e6e',
      buttonBg: '#c2185b',
      buttonText: '#ffffff',
    },
  },
  monochrome: {
    id: 'monochrome',
    name: 'Monochrome',
    colors: {
      background: '#ffffff',
      surface: '#f4f4f4',
      primary: '#111111',
      accent: '#555555',
      text: '#111111',
      textMuted: '#777777',
      buttonBg: '#111111',
      buttonText: '#ffffff',
    },
  },
};

// ============================================================
// Font Themes
// ============================================================

export interface FontThemeConfig {
  id: FontThemeId;
  name: string;
  headingFont: string;
  bodyFont: string;
  googleImport: string;
}

export const FONT_THEMES: Record<Exclude<FontThemeId, 'custom'>, FontThemeConfig> = {
  modernClean: {
    id: 'modernClean',
    name: 'Modern Clean',
    headingFont: 'Inter',
    bodyFont: 'Inter',
    googleImport: 'Inter:wght@300;400;600;700;900',
  },
  classicSerif: {
    id: 'classicSerif',
    name: 'Classic Serif',
    headingFont: 'Playfair Display',
    bodyFont: 'Source Sans 3',
    googleImport: 'Playfair+Display:wght@400;700;900&family=Source+Sans+3:wght@300;400;600',
  },
  boldStatement: {
    id: 'boldStatement',
    name: 'Bold Statement',
    headingFont: 'Oswald',
    bodyFont: 'Lato',
    googleImport: 'Oswald:wght@400;500;700&family=Lato:wght@300;400;700',
  },
  artistic: {
    id: 'artistic',
    name: 'Artistic',
    headingFont: 'Cormorant Garamond',
    bodyFont: 'Nunito',
    googleImport: 'Cormorant+Garamond:wght@400;600;700&family=Nunito:wght@300;400;600',
  },
  friendly: {
    id: 'friendly',
    name: 'Friendly',
    headingFont: 'Poppins',
    bodyFont: 'Poppins',
    googleImport: 'Poppins:wght@300;400;600;700;800',
  },
  editorial: {
    id: 'editorial',
    name: 'Editorial',
    headingFont: 'DM Serif Display',
    bodyFont: 'DM Sans',
    googleImport: 'DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;700',
  },
};

// ============================================================
// Available Google Fonts (for custom override dropdowns)
// ============================================================

export const AVAILABLE_FONTS: string[] = [
  'Inter',
  'Playfair Display',
  'Source Sans 3',
  'Oswald',
  'Lato',
  'Cormorant Garamond',
  'Nunito',
  'Poppins',
  'DM Serif Display',
  'DM Sans',
  'Montserrat',
  'Raleway',
  'Merriweather',
  'Roboto',
  'Open Sans',
  'Bebas Neue',
  'Archivo',
  'Space Grotesk',
];

// ============================================================
// Link Categories
// ============================================================

export interface LinkCategoryConfig {
  id: LinkCategory;
  label: string;
  icon: string;
}

export const LINK_CATEGORIES: LinkCategoryConfig[] = [
  { id: 'spotify', label: 'Spotify', icon: 'spotify' },
  { id: 'apple-music', label: 'Apple Music', icon: 'apple' },
  { id: 'youtube-music', label: 'YouTube Music', icon: 'youtube' },
  { id: 'soundcloud', label: 'SoundCloud', icon: 'soundcloud' },
  { id: 'tidal', label: 'Tidal', icon: 'tidal' },
  { id: 'amazon-music', label: 'Amazon Music', icon: 'amazon' },
  { id: 'bandcamp', label: 'Bandcamp', icon: 'bandcamp' },
  { id: 'instagram', label: 'Instagram', icon: 'instagram' },
  { id: 'tiktok', label: 'TikTok', icon: 'tiktok' },
  { id: 'twitter', label: 'X / Twitter', icon: 'twitter' },
  { id: 'facebook', label: 'Facebook', icon: 'facebook' },
  { id: 'youtube', label: 'YouTube', icon: 'youtube' },
  { id: 'merch', label: 'Merch Store', icon: 'merch' },
  { id: 'tickets', label: 'Tickets', icon: 'tickets' },
  { id: 'website', label: 'Website', icon: 'globe' },
  { id: 'other', label: 'Other', icon: 'link' },
];

/** Categories that represent streaming music platforms (for the "Listen" button). */
export const STREAMING_CATEGORIES: LinkCategory[] = [
  'spotify',
  'apple-music',
  'youtube-music',
  'soundcloud',
  'tidal',
  'amazon-music',
  'bandcamp',
];

// ============================================================
// Button Styles
// ============================================================

export const BUTTON_STYLES: ButtonStyle[] = ['filled', 'outlined', 'ghost', 'soft'];

// ============================================================
// Profile Display Modes
// ============================================================

export interface ProfileDisplayModeConfig {
  id: ProfileDisplayMode;
  name: string;
  description: string;
}

export const PROFILE_DISPLAY_MODES: ProfileDisplayModeConfig[] = [
  { id: 'circle', name: 'Circle', description: 'Circular profile photo' },
  { id: 'full', name: 'Full / Logo', description: 'Wide image or logo' },
];

// ============================================================
// SVG Icons
// ============================================================

export const SVG_ICONS: Record<string, string> = {
  spotify: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>`,

  apple: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>`,

  youtube: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>`,

  soundcloud: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.05-.1-.1-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.172 1.308c.013.06.045.094.104.094.057 0 .09-.037.104-.094l.199-1.308-.2-1.332c-.014-.057-.047-.094-.104-.094m1.8-1.143c-.063 0-.104.05-.11.112l-.217 2.452.218 2.364c.006.064.047.112.11.112.062 0 .104-.048.11-.112l.244-2.364-.244-2.452c-.006-.062-.048-.112-.11-.112m.899-.057c-.073 0-.12.058-.127.125l-.2 2.519.2 2.418c.007.066.054.125.127.125.072 0 .12-.06.126-.125l.224-2.418-.224-2.519c-.007-.067-.054-.125-.126-.125m.899-.112c-.084 0-.135.065-.14.14l-.182 2.63.182 2.476c.005.074.056.14.14.14.083 0 .135-.066.14-.14l.204-2.476-.204-2.63c-.005-.075-.057-.14-.14-.14m.9-.1c-.094 0-.15.074-.154.154l-.165 2.73.165 2.52c.004.08.06.154.154.154.093 0 .15-.074.154-.154l.186-2.52-.186-2.73c-.004-.08-.06-.154-.154-.154m.898-.048c-.104 0-.166.08-.168.166l-.149 2.778.149 2.546c.002.088.064.166.168.166s.165-.078.168-.166l.167-2.546-.167-2.778c-.003-.086-.064-.166-.168-.166m.9.01c-.114 0-.18.09-.182.18l-.131 2.588.131 2.572c.002.094.068.18.182.18.113 0 .18-.086.181-.18l.148-2.572-.148-2.588c-.001-.09-.068-.18-.181-.18m.9-.082c-.124 0-.194.098-.196.196l-.114 2.67.114 2.593c.002.1.072.196.196.196.123 0 .194-.096.196-.196l.128-2.593-.128-2.67c-.002-.098-.073-.196-.196-.196m2.705-1.39c-.264 0-.518.042-.759.118C9.16 5.4 7.2 3.6 4.92 3.6c-.6 0-1.188.12-1.728.36-.24.108-.312.216-.312.432v10.08c0 .216.168.396.384.408h8.16c1.584 0 2.868-1.296 2.868-2.88s-1.284-2.88-2.868-2.88"/></svg>`,

  tidal: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.012 3.992L8.008 7.996 12.012 12l4.004-4.004zM4.004 7.996L0 12l4.004 4.004L8.008 12zm15.992 0L15.992 12l4.004 4.004L24 12zM12.012 12l-4.004 4.004 4.004 4.004 4.004-4.004z"/></svg>`,

  amazon: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M.045 18.02c.072-.116.187-.124.348-.022 2.344 1.474 4.882 2.21 7.613 2.21 1.86 0 3.681-.355 5.463-1.068.264-.101.396.022.396.22 0 .22-.12.396-.264.484-2.281 1.23-4.758 1.846-7.414 1.846-2.927 0-5.463-.726-7.613-2.174-.18-.116-.22-.264-.132-.396l.003-.1zM22.07 16.46c-.12-.187-.396-.264-.748-.132-1.107.396-2.15.594-3.12.594-2.64 0-4.51-1.107-5.617-2.21-.132-.132-.264-.132-.396 0-.132.132-.132.264 0 .396 1.474 1.474 3.681 2.574 6.321 2.574 1.107 0 2.346-.22 3.453-.726.264-.132.396-.264.396-.396 0-.044-.044-.066-.132-.066l-.157-.034z"/><path d="M14.4 6.89c0-1.474.594-2.574 1.846-2.574.66 0 1.186.264 1.582.792.264.396.396.924.396 1.65v4.224c0 .66-.044 1.186-.132 1.582-.132.528-.396.924-.792 1.186-.396.264-.858.396-1.386.396-.66 0-1.186-.22-1.582-.66-.396-.44-.594-1.054-.594-1.846V6.89h.66zm-3.12 4.884c0 1.474.462 2.574 1.386 3.318.792.66 1.846.99 3.12.99 1.386 0 2.442-.396 3.186-1.186.66-.726.99-1.714.99-2.97V6.89c0-1.386-.396-2.442-1.186-3.186C17.99 3.044 16.934 2.714 15.66 2.714c-1.386 0-2.442.396-3.186 1.186-.66.726-.99 1.714-.99 2.97v4.904h-.204z"/></svg>`,

  bandcamp: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M0 18.75l7.437-13.5H24l-7.438 13.5z"/></svg>`,

  instagram: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>`,

  tiktok: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>`,

  twitter: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`,

  facebook: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>`,

  merch: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>`,

  tickets: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 013-3h14a3 3 0 013 3v0a3 3 0 01-3 3v1a3 3 0 013 3v0a3 3 0 01-3 3H5a3 3 0 01-3-3v0a3 3 0 013-3v-1a3 3 0 01-3-3z"/><path d="M13 6v2m0 8v2m0-6v.01"/></svg>`,

  globe: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>`,

  link: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>`,

  music: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>`,

  calendar: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`,

  chevronDown: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`,

  play: `<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>`,

  headphones: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18v-6a9 9 0 0118 0v6"/><path d="M21 19a2 2 0 01-2 2h-1a2 2 0 01-2-2v-3a2 2 0 012-2h3zM3 19a2 2 0 002 2h1a2 2 0 002-2v-3a2 2 0 00-2-2H3z"/></svg>`,

  mapPin: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
};

export function getIconSvg(iconName: string): string {
  return SVG_ICONS[iconName] || SVG_ICONS.link;
}

// ============================================================
// Default Form State
// ============================================================

export function createDefaultMicrositeFormState(): MicrositeFormState {
  return {
    slug: '',
    artist_name: '',
    show_hero_artist_name: true,
    tagline: '',
    bio: '',
    layout: 'bold',
    palette_id: 'midnight',
    colors: { ...COLOR_PALETTES.midnight.colors },
    font_theme_id: 'modernClean',
    fonts: {
      headingFont: 'Inter',
      bodyFont: 'Inter',
    },
    base_font_size: 16,
    border_radius: 12,
    button_style: 'filled',
    hero_image_key: null,
    hero_image_url: null,
    profile_image_key: null,
    profile_image_url: null,
    featured_image_key: null,
    featured_image_url: null,
    profile_display_mode: 'circle',
    featured_title: '',
    featured_subtitle: '',
    featured_url: '',
    links: [],
    show_events: false,
    events: [],
    email_capture: {
      enabled: false,
      prompt_text: 'Join my mailing list',
      button_label: 'Subscribe',
    },
    published: false,
  };
}
